import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatarBRL } from "../../helpers/dinheiro";
import { cn } from "../../lib/utils";

const dataCurta = (valor) =>
	valor ? new Date(valor).toLocaleDateString("pt-BR") : null;

const quantidadeLegivel = (valor) =>
	Number(valor).toLocaleString("pt-BR", { maximumFractionDigits: 3 });

// Itens, busca de produto e valores (desconto, frete, prazo, condição) de uma
// venda. Uma regra de edição para os três lugares que montam venda: o painel
// da conversa (`compacto`, 360px), o cartão do Kanban/diálogos (`linha`) e a
// página da venda (`pagina`). Só o arranjo muda com a largura.
//
// Fora do rascunho tudo vira texto: o backend recusa a mudança (409), e um
// campo aberto prometeria o contrário.
const ItensDaVenda = ({
	orcamento,
	onOrcamento,
	editavel,
	variante = "linha",
	// { [productId]: saldo − reservado } — só a página tem (vem de /sales/:id).
	disponivel,
}) => {
	const compacto = variante === "compacto";
	const pagina = variante === "pagina";

	const [ocupado, setOcupado] = useState(false);
	const [busca, setBusca] = useState("");
	const [produtos, setProdutos] = useState([]);
	const [quantidade, setQuantidade] = useState("1");
	// Venda de 5 itens não pode ser 5 buscas do zero: depois de adicionar, o
	// foco volta para cá.
	const buscaRef = useRef(null);
	// Item "sob consulta" entra sem preço e ganha o foco no campo de preço:
	// cliente recebendo item a R$ 0,00 derruba a confiança na loja.
	const [precoPendenteId, setPrecoPendenteId] = useState(null);

	useEffect(() => {
		if (!busca.trim()) {
			setProdutos([]);
			return undefined;
		}

		const t = setTimeout(async () => {
			try {
				const { data } = await api.get("/products", {
					params: { searchParam: busca, onlyActive: "true" },
				});
				setProdutos(data.products || []);
			} catch (err) {
				toastError(err);
			}
		}, 300);

		return () => clearTimeout(t);
	}, [busca]);

	// Toda mudança devolve o orçamento inteiro (total recalculado no servidor).
	const salvar = async (pedido) => {
		setOcupado(true);
		try {
			const { data } = await pedido();
			onOrcamento(data);
			return data;
		} catch (err) {
			toastError(err);
			return null;
		} finally {
			setOcupado(false);
		}
	};

	const adicionar = async (produto) => {
		const data = await salvar(() =>
			api.post(`/quotes/${orcamento.id}/items`, {
				productId: produto.id,
				quantity: quantidade,
			}),
		);
		if (!data) return;

		setBusca("");
		setProdutos([]);
		setQuantidade("1");

		if (produto.price === null || produto.price === undefined) {
			// O item entrou "sob consulta": leva o foco para o preço dele.
			const semPreco = (data.items || [])
				.filter((i) => !(Number(i.unitPrice) > 0))
				.pop();
			setPrecoPendenteId(semPreco?.id || null);
		} else {
			// Próximo item: foco de volta na busca, sem tocar no mouse.
			buscaRef.current?.focus();
		}
	};

	const mudarItem = (item, campo, valor) =>
		salvar(() =>
			api.put(`/quotes/${orcamento.id}/items/${item.id}`, { [campo]: valor }),
		);

	const remover = (item) =>
		salvar(() => api.delete(`/quotes/${orcamento.id}/items/${item.id}`));

	// Um caminho para todo campo do próprio orçamento (desconto, frete, prazo,
	// condição): eram quatro funções iguais trocando só a chave.
	const mudarCampo = (campo, valor) =>
		salvar(() => api.put(`/quotes/${orcamento.id}`, { [campo]: valor }));

	const itens = orcamento.items || [];
	const temLocacao = itens.some((i) => i.rentalStart);

	const linhaDoItem = (item) => {
		const campoQuantidade = !editavel ? (
			<span
				className={cn(
					"text-sm tabular-nums text-muted-foreground",
					!compacto && "w-16 text-right",
				)}
			>
				{quantidadeLegivel(item.quantity)}
			</span>
		) : (
			<Input
				className={cn("h-8 text-right", compacto ? "w-14" : "w-16")}
				defaultValue={Number(item.quantity)}
				inputMode="decimal"
				aria-label={`Quantidade de ${item.description}`}
				onBlur={(e) =>
					Number(e.target.value) !== Number(item.quantity) &&
					mudarItem(item, "quantity", e.target.value)
				}
			/>
		);

		const campoPreco = !editavel ? (
			<span
				className={cn(
					"text-sm tabular-nums text-muted-foreground",
					!compacto && "w-24 text-right",
				)}
			>
				{formatarBRL(item.unitPrice)}
			</span>
		) : (
			<Input
				className={cn(
					"h-8 text-right",
					compacto ? "w-20" : "w-24",
					!(Number(item.unitPrice) > 0) &&
						"border-amber-500 focus-visible:ring-amber-500",
				)}
				defaultValue={Number(item.unitPrice).toFixed(2)}
				inputMode="decimal"
				autoFocus={item.id === precoPendenteId}
				aria-label={`Preço de ${item.description}`}
				onBlur={(e) => {
					if (item.id === precoPendenteId) setPrecoPendenteId(null);
					if (Number(e.target.value) !== Number(item.unitPrice))
						mudarItem(item, "unitPrice", e.target.value);
				}}
			/>
		);

		const botaoRemover = editavel && (
			<Button
				variant="ghost"
				size="icon"
				className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
				aria-label={`Remover ${item.description}`}
				onClick={() => remover(item)}
				disabled={ocupado}
			>
				<Trash2 className="h-4 w-4" />
			</Button>
		);

		// No painel de 360px o item vira um cartãozinho: nome e total na linha
		// de cima, "qtd × preço" e a lixeira embaixo. Numa linha só, os campos
		// fixos engoliam a largura e o nome sumia com zero pixel.
		if (compacto) {
			return (
				<div key={item.id} className="space-y-1.5 rounded-lg border bg-muted/40 p-2">
					<div className="flex items-baseline justify-between gap-2">
						<span
							className="min-w-0 truncate text-sm font-medium"
							title={item.description}
						>
							{item.description}
						</span>
						<span className="shrink-0 text-sm font-semibold tabular-nums">
							{formatarBRL(item.total)}
						</span>
					</div>
					<div className="flex items-center gap-1.5">
						{campoQuantidade}
						<span className="text-xs text-muted-foreground">×</span>
						{campoPreco}
						<span className="ml-auto">{botaoRemover}</span>
					</div>
				</div>
			);
		}

		// Disponível = saldo − reservado. Negativo é venda prometida sem
		// mercadoria: aviso, não trava (a contagem pode estar atrasada).
		const saldo =
			pagina && disponivel && item.productId != null
				? disponivel[item.productId]
				: undefined;

		return (
			<div
				key={item.id}
				className={cn(
					"flex items-center gap-2",
					pagina && "flex-wrap border-b py-2 last:border-b-0 sm:flex-nowrap",
				)}
			>
				<div className="min-w-0 flex-1">
					<span className="block truncate text-sm" title={item.description}>
						{item.description}
					</span>
					{pagina && (item.rentalStart || saldo !== undefined) && (
						<span className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
							{item.rentalStart && (
								<span>
									Locação {dataCurta(item.rentalStart)} a{" "}
									{dataCurta(item.rentalEnd)}
								</span>
							)}
							{saldo !== undefined && (
								<span
									className={cn(
										saldo < 0 && "inline-flex items-center gap-1 font-medium text-destructive",
									)}
								>
									{saldo < 0 && <AlertTriangle className="h-3 w-3" />}
									{quantidadeLegivel(saldo)} disponíveis
								</span>
							)}
						</span>
					)}
				</div>
				{campoQuantidade}
				{campoPreco}
				<span className="w-24 shrink-0 text-right text-sm tabular-nums">
					{formatarBRL(item.total)}
				</span>
				{botaoRemover}
			</div>
		);
	};

	const buscaDeProduto = editavel && (
		<div className="space-y-1.5">
			<div className="flex gap-2">
				<Input
					ref={buscaRef}
					className="h-8 flex-1"
					value={busca}
					autoFocus={pagina && itens.length === 0}
					onChange={(e) => setBusca(e.target.value)}
					onKeyDown={(e) => {
						// Enter adiciona o primeiro resultado: 5 itens sem mouse.
						if (e.key === "Enter" && produtos.length && !ocupado) {
							e.preventDefault();
							adicionar(produtos[0]);
						}
					}}
					placeholder="Buscar produto para adicionar"
				/>
				<Input
					className="h-8 w-16 text-right"
					value={quantidade}
					onChange={(e) => setQuantidade(e.target.value)}
					inputMode="decimal"
					aria-label="Quantidade do item novo"
				/>
			</div>
			{produtos.length > 0 && (
				<div className="max-h-48 overflow-y-auto rounded-lg border">
					{produtos.map((p) => (
						<button
							key={p.id}
							type="button"
							disabled={ocupado}
							onClick={() => adicionar(p)}
							className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
						>
							<Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
							<span className="min-w-0 flex-1 truncate">{p.name}</span>
							{/* Disponível, não saldo: o que já foi vendido e não saiu
							    tem dono. Aviso, não bloqueio — a contagem pode estar
							    atrasada e o atendimento não pode parar por isso. */}
							{p.trackStock &&
								(() => {
									const livre =
										Number(p.stockBalance) - Number(p.stockReserved || 0);

									return livre <= 0 ? (
										<span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-destructive/10 px-1.5 py-0.5 text-xs font-medium text-destructive">
											<AlertTriangle className="h-3 w-3" />
											sem estoque
										</span>
									) : (
										<span className="shrink-0 text-xs text-muted-foreground">
											{`${quantidadeLegivel(livre)} disponíveis`}
										</span>
									);
								})()}
							<span className="shrink-0 text-xs text-muted-foreground">
								{p.price === null || p.price === undefined
									? "sob consulta"
									: formatarBRL(p.price)}
							</span>
						</button>
					))}
				</div>
			)}
		</div>
	);

	// Os campos do próprio orçamento. `id` por venda: a página e um diálogo
	// abertos juntos não podem repetir id de rótulo.
	const campo = (chave, rotulo, props) => {
		const id = `venda-${orcamento.id}-${chave}`;

		return (
			<div
				className={cn(
					pagina ? "space-y-1" : "flex items-center justify-end gap-2",
					props.largo && (pagina ? "sm:col-span-2" : "!block space-y-1"),
				)}
			>
				<Label htmlFor={id} className="text-xs">
					{rotulo}
				</Label>
				<Input
					id={id}
					className={cn(
						"h-8",
						!props.largo && "text-right",
						!props.largo && !pagina && "w-24",
					)}
					defaultValue={props.valor}
					placeholder={props.placeholder}
					inputMode={props.inputMode}
					disabled={!editavel || ocupado}
					onBlur={(e) =>
						props.mudou(e.target.value) && mudarCampo(chave, e.target.value)
					}
				/>
			</div>
		);
	};

	const reais = (valor) =>
		valor === null || valor === undefined ? "" : Number(valor).toFixed(2);

	const valores = (
		<div
			className={cn(
				pagina ? "grid gap-3 sm:grid-cols-2" : "space-y-2 border-t pt-2",
			)}
		>
			{campo("discount", "Desconto", {
				valor: Number(orcamento.discount).toFixed(2),
				inputMode: "decimal",
				mudou: (v) => Number(v) !== Number(orcamento.discount),
			})}
			{/* Frete entra no total depois do desconto: desconto é na mercadoria,
			    não na entrega. */}
			{campo("shippingCost", "Frete", {
				valor: reais(orcamento.shippingCost),
				placeholder: "0,00",
				inputMode: "decimal",
				mudou: (v) => Number(v || 0) !== Number(orcamento.shippingCost || 0),
			})}
			{/* Caução: garantia da locação, fora do total. */}
			{pagina &&
				temLocacao &&
				campo("depositAmount", "Caução (fora do total)", {
					valor: reais(orcamento.depositAmount),
					placeholder: "0,00",
					inputMode: "decimal",
					mudou: (v) =>
						Number(v || 0) !== Number(orcamento.depositAmount || 0),
				})}
			{campo("deliveryDays", "Prazo (dias)", {
				valor: orcamento.deliveryDays ?? "",
				placeholder: "—",
				inputMode: "numeric",
				mudou: (v) => String(v) !== String(orcamento.deliveryDays ?? ""),
			})}
			{campo("paymentTerms", "Condição de pagamento", {
				largo: true,
				valor: orcamento.paymentTerms || "",
				placeholder: "À vista, 3x sem juros…",
				mudou: (v) => v !== (orcamento.paymentTerms || ""),
			})}
		</div>
	);

	const semPreco = editavel && itens.some((i) => !(Number(i.unitPrice) > 0));

	const total = (
		<div className="flex items-center justify-between border-t pt-2">
			<span className="text-sm font-medium">Total</span>
			<span className="text-lg font-semibold tabular-nums">
				{formatarBRL(orcamento.total)}
			</span>
		</div>
	);

	const avisoSemPreco = semPreco && (
		<p className="flex items-center gap-1.5 text-xs text-amber-600">
			<AlertTriangle className="h-3.5 w-3.5 shrink-0" />
			Há item sem preço — informe antes de enviar.
		</p>
	);

	const lista = (
		<div className={compacto ? "space-y-2" : pagina ? "" : "space-y-1.5"}>
			{itens.map(linhaDoItem)}
			{itens.length === 0 && (
				<p className="py-2 text-center text-xs text-muted-foreground">
					{editavel
						? "Nenhum item ainda. Busque um produto abaixo."
						: "Nenhum item."}
				</p>
			)}
		</div>
	);

	if (pagina) {
		return (
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
				<div className="min-w-0 space-y-3">
					{lista}
					{buscaDeProduto}
					{avisoSemPreco}
				</div>
				<div className="space-y-3 lg:border-l lg:pl-6">
					{valores}
					{total}
					{Number(orcamento.depositAmount) > 0 && (
						<p className="text-xs text-muted-foreground">
							Mais {formatarBRL(orcamento.depositAmount)} de caução, devolvida
							no fim da locação.
						</p>
					)}
				</div>
			</div>
		);
	}

	return (
		<>
			{lista}
			{buscaDeProduto}
			{valores}
			{total}
			{avisoSemPreco}
		</>
	);
};

export default ItensDaVenda;
